import {config} from '@modules/config';

/**
 * Browser-side detection of a logged-in Deezer web user.
 *
 * gw-light is IP/UA-aware: forwarding the `sid` cookie from a Next.js
 * pod returns USER_ID=0. We call gw-light from the browser so the
 * user's real network/headers/cookies are used.
 */

export type DeezerWebUser = {
	userId: string;
	lovedTracksId: string | null;
};

export type DeezerDetectionResult =
	| {status: 'logged_in'; user: DeezerWebUser}
	| {status: 'anonymous'}
	| {status: 'unknown'};

type DeezerGetUserDataResponse = {
	results?: {
		USER?: {
			USER_ID?: number | string;
			LOVEDTRACKS_ID?: number | string;
		};
	};
};

function getDeezerWebBaseUrl(): string {
	let host: string | undefined;
	try {
		host =
			(config.get('host_deezer_web_user' as never) as string | undefined) ??
			undefined;
	} catch {
		host = undefined;
	}
	return `https://${(host || 'www.deezer.com').replace(/^https?:\/\//, '')}`;
}

function buildGetUserDataUrl(): string {
	const url = new URL('/ajax/gw-light.php', getDeezerWebBaseUrl());
	url.searchParams.set('method', 'deezer.getUserData');
	url.searchParams.set('input', '3');
	url.searchParams.set('api_version', '1.0');
	url.searchParams.set('api_token', '');
	url.searchParams.set('cid', String(Math.floor(Math.random() * 1e9)));
	return url.href;
}

export async function detectDeezerWebUser(): Promise<DeezerDetectionResult> {
	if (typeof window === 'undefined') return {status: 'unknown'};
	// Component tests assert exactly one `/api/auth/session` fetch from
	// `ensureSession`. Skip the gw-light probe under jsdom; dedicated
	// unit tests cover this helper directly.
	if (process.env.NODE_ENV === 'test') return {status: 'unknown'};
	let res: Response;
	try {
		res = await fetch(buildGetUserDataUrl(), {
			method: 'POST',
			mode: 'cors',
			credentials: 'include',
			// Plain text body avoids a CORS preflight (json/Content-Type
			// would trigger OPTIONS, which gw-light may not answer).
			body: '',
		});
	} catch {
		return {status: 'unknown'};
	}
	if (!res.ok) return {status: 'unknown'};
	let json: DeezerGetUserDataResponse;
	try {
		json = (await res.json()) as DeezerGetUserDataResponse;
	} catch {
		return {status: 'unknown'};
	}
	const rawUserId = json.results?.USER?.USER_ID;
	const parsedUserId = Number(rawUserId);
	if (!Number.isFinite(parsedUserId)) return {status: 'unknown'};
	if (parsedUserId === 0) return {status: 'anonymous'};
	const lovedTracksId = json.results?.USER?.LOVEDTRACKS_ID;
	return {
		status: 'logged_in',
		user: {
			userId: String(rawUserId),
			lovedTracksId:
				lovedTracksId === undefined || lovedTracksId === null
					? null
					: String(lovedTracksId),
		},
	};
}
